"use client";

import { FEEDBACK_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { Stars } from "./feedback-stars";

type TFeedbackFormProps = {
	currentRating: number;
	onRating: (newRating: number) => void;
	onSubmit: () => void;
};

const FeedbackForm = ({ currentRating, onRating, onSubmit }: TFeedbackFormProps) => {
	const [form, setForm] = useState({ aboutTheSite: "", whatCouldBeBetter: "" });
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");

	const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setForm({
			...form,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);

		if (currentRating % 0.5 !== 0 || currentRating < 0 || currentRating > 5) {
			setErrorMessage("Rating is invalid");
			setIsLoading(false);
			return;
		}

		const url = new URL(FEEDBACK_API);
		const response = await fetch(url, {
			method: "POST",
			body: JSON.stringify({ rating: currentRating, ...form }),
		});

		const data = await response.json();
		const message = data?.message;

		if (response.ok) {
			toast.success(message);
			onSubmit();
		} else {
			toast.error(message);
		}

		setIsLoading(false);
	};

	return (
		<form onSubmit={handleSubmit}>
			<h4 className="mt-4 mb-1 text-gray-600">How likely are you to recommend our website?</h4>
			<Stars className="justify-start gap-x-6" rating={currentRating} setRating={onRating} />

			<div className="flex flex-col gap-2">
				<label className="mt-6 mb-1 text-gray-600">What do you like about the site?</label>
				<textarea
					name="aboutTheSite"
					placeholder="Type here"
					onChange={onChange}
					className="w-full p-4 border rounded-lg border-slate-300 focus:outline-gray-200"
				/>

				<label className="mt-6 mb-1 text-gray-600">
					What could be done better? Any other comment
				</label>
				<textarea
					name="whatCouldBeBetter"
					onChange={onChange}
					placeholder="Type here"
					className="w-full p-4 border rounded-lg border-slate-300 focus:outline-gray-200"
				/>

				<button className="px-8 py-4 mt-6 text-lg font-semibold text-center text-white rounded-lg bg-brand-primary">
					{isLoading ? (
						<Loader2Icon className="w-8 h-8 mx-auto animate-spin stroke-white" />
					) : (
						"Submit"
					)}
				</button>
			</div>

			<p
				className={cn(
					"text-sm text-red-400 transition-opacity duration-150 ease-linear",
					errorMessage ? "opacity-100" : "opacity-0"
				)}
			>
				{errorMessage}
			</p>
		</form>
	);
};

export default FeedbackForm;
